import { html, nothing, type TemplateResult } from "lit";
import { cardBackStyle } from "../cosmetics";
import type { Pile, PileDest, PublicArrangement, PublicCard } from "../protocol";

// The arrangement question (#250): where each card a player looks at goes, a pile at a time, as
// cards indices per pile. 401.4: a library pile lists its cards top first. As on Arena, the cards
// are dragged between the piles across the dimmed table, and along a library pile to order it.

export type ArrangementHost = {
  cardArt: (c: PublicCard) => TemplateResult;
  // The viewer's card back, drawn on their library where a pile goes into it.
  cardBack: string | null;
  // The piles answered so far; a new question starts from its suggestion.
  piles: number[][];
  setPiles: (piles: number[][]) => void;
  // A press on a card, which the board turns into a drag; the card it's dragging, if any.
  press: (card: number, e: PointerEvent) => void;
  lifted: number | null;
  answer: () => void;
  // The overlay's way out (its Cancel) and its button to look at the battlefield under it.
  foot: TemplateResult;
  peek: TemplateResult;
  peeking: boolean;
};

export function suggestedPiles(a: PublicArrangement): number[][] {
  return a.suggested.map((pile) => [...pile]);
}

export function placedInPile(piles: number[][], card: number, to: number, at: number): number[][] {
  return piles.map((pile, j) => {
    const without = pile.filter((c) => c !== card);
    return j === to ? [...without.slice(0, at), card, ...without.slice(at)] : without;
  });
}

// The pile under the pointer that may take the card, and where along it the card lands.
export function arrangementDrop(root: ParentNode, a: PublicArrangement, card: number, x: number, y: number): { pile: number; at: number } | null {
  const piles = [...root.querySelectorAll<HTMLElement>(".arrange-pile")];
  const pile = piles.findIndex((el) => {
    const r = el.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  });
  if (pile === -1 || !a.piles[pile]?.eligible.includes(card)) return null;
  const others = [...piles[pile]!.querySelectorAll<HTMLElement>(".arrange-card")].filter((el) => Number(el.dataset["card"]) !== card);
  const at = others.filter((el) => { const r = el.getBoundingClientRect(); return r.left + r.width / 2 < x; }).length;
  return { pile, at };
}

export function arrangementFits(a: PublicArrangement, piles: number[][]): boolean {
  return a.piles.every((p, j) => {
    const n = piles[j]?.length ?? 0;
    return n >= p.at_least && n <= p.at_most;
  });
}

// Prefilled with the suggestion, so Done accepts it in one click. A card is dragged to any other
// pile it may go to, or along its own; its buttons do the same without a drag. A pile into the
// library lies against the library itself, face down: on top, the cards run into it; on the bottom,
// out of it.
export function renderArrangement(a: PublicArrangement, host: ArrangementHost): TemplateResult {
  const name = (i: number) => a.cards[i]?.characteristics.name ?? `#${i}`;
  const piles = host.piles;
  const misfit = misfitPile(a, piles);
  const lifted = host.lifted;
  const stop = (e: Event) => e.stopPropagation();
  const decks = a.piles.filter((p) => intoLibrary(p.to)).length;
  const deck = (under: "top" | "bottom") => html`<span class="arrange-deck under-${under} with-back" style=${cardBackStyle(host.cardBack)} aria-hidden="true"></span>`;
  return html`<div class="pop prompt choose-one arrangement ${lifted !== null ? "dragging" : ""} ${host.peeking ? "peeking" : ""}" role="dialog"
      aria-label="Where do these cards go?" style="--n: ${a.cards.length + decks / 2}">
    <header class="choose-heading">
      <h3>Where Do These Cards Go?</h3>
      <div class="sub ${misfit ? "misfit" : ""}">${misfit ?? "Drag each card to where it goes"}</div>
    </header>
    <div class="choose-options arrange-piles">
      ${a.piles.map((p, j) => {
        const cards = piles[j] ?? [];
        const ordered = inChosenOrder(p.to) && cards.length > 1;
        const takes = lifted !== null && !cards.includes(lifted) && p.eligible.includes(lifted);
        const place = PILE_PLACE[p.to.kind];
        return html`<section class="arrange-pile ${takes ? "takes" : ""} ${lifted !== null && !takes && !cards.includes(lifted) ? "refuses" : ""}"
            aria-label=${[place.name, place.how].filter(Boolean).join(", ")}>
          <header class="arrange-head">
            <span class="arrange-place">${place.name}</span>
            ${place.how || pileLimit(p, a.cards.length) ? html`<span class="arrange-how ${pileHolds(p, cards.length) ? "" : "off"}">${[place.how, pileLimit(p, a.cards.length)].filter(Boolean).join(", ")}</span>` : nothing}
          </header>
          <div class="arrange-tray" role="list">
            ${p.to.kind !== "OnTop" && intoLibrary(p.to) ? deck("bottom") : nothing}
            ${cards.length === 0 ? html`<span class="arrange-empty">Drag a card here</span>` : nothing}
            ${cards.map((card, k) => html`<div class="arrange-card ${lifted === card ? "lifted" : ""}" role="listitem" aria-label=${name(card)} data-card=${card}
                @pointerdown=${(e: PointerEvent) => host.press(card, e)}>
              <span class="option-card">${a.cards[card] ? host.cardArt(a.cards[card]) : nothing}</span>
              <span class="arrange-moves">
                ${ordered ? html`<button class="btn" aria-label="Move ${name(card)} up" ?disabled=${k === 0} @pointerdown=${stop}
                  @click=${() => host.setPiles(placedInPile(piles, card, j, k - 1))}>‹</button>` : nothing}
                ${a.piles.map((q, t) => t !== j && q.eligible.includes(card)
                  ? html`<button class="btn" @pointerdown=${stop} @click=${() => host.setPiles(placedInPile(piles, card, t, piles[t]?.length ?? 0))}>${PILE_MOVE[q.to.kind]}</button>` : nothing)}
                ${ordered ? html`<button class="btn" aria-label="Move ${name(card)} down" ?disabled=${k === cards.length - 1} @pointerdown=${stop}
                  @click=${() => host.setPiles(placedInPile(piles, card, j, k + 1))}>›</button>` : nothing}
              </span>
            </div>`)}
            ${p.to.kind === "OnTop" ? deck("top") : nothing}
          </div>
        </section>`;
      })}
    </div>
    <div class="choose-actions">
      <div class="choose-buttons">
        ${host.peek}
        <button class="btn done" ?disabled=${misfit !== null} @click=${host.answer}>Done</button>
      </div>
      ${host.foot}
    </div>
  </div>`;
}

function inChosenOrder(to: PileDest): boolean {
  return to.kind === "OnTop" || to.kind === "OnBottom";
}

function intoLibrary(to: PileDest): boolean {
  return to.kind === "OnTop" || to.kind === "OnBottom" || to.kind === "OnBottomInRandomOrder";
}

const PILE_PLACE: Record<PileDest["kind"], { name: string; how?: string }> = {
  OnTop: { name: "Top of your library" },
  OnBottom: { name: "Bottom of your library" },
  OnBottomInRandomOrder: { name: "Bottom of your library", how: "in a random order" },
  IntoHand: { name: "Your hand" },
  IntoGraveyard: { name: "Your graveyard" },
  IntoExile: { name: "Exile" },
  OntoBattlefield: { name: "Battlefield" },
  OntoBattlefieldTapped: { name: "Battlefield", how: "tapped" },
  OntoBattlefieldAttacking: { name: "Battlefield", how: "tapped and attacking" },
  Manifested: { name: "Manifested", how: "face down as a 2/2" },
  Cloaked: { name: "Cloaked", how: "face down as a 2/2 with ward {2}" },
  StaysInHand: { name: "Stays in your hand" },
  StaysWhereMilled: { name: "Stays where milled" },
};

const PILE_MOVE: Record<PileDest["kind"], string> = {
  OnTop: "To top",
  OnBottom: "To bottom",
  OnBottomInRandomOrder: "To bottom",
  IntoHand: "To hand",
  IntoGraveyard: "To graveyard",
  IntoExile: "Exile",
  OntoBattlefield: "To battlefield",
  OntoBattlefieldTapped: "To battlefield",
  OntoBattlefieldAttacking: "Attack",
  Manifested: "Manifest",
  Cloaked: "Cloak",
  StaysInHand: "Keep",
  StaysWhereMilled: "Leave",
};

function pileHolds(p: Pile, holding: number): boolean {
  return holding >= p.at_least && holding <= p.at_most;
}

// A pile that takes any number of the cards, as the rest does, needs no limit.
function pileLimit(p: Pile, cards: number): string | null {
  if (p.at_least === 0 && p.at_most >= cards) return null;
  return p.at_least === p.at_most ? `exactly ${p.at_most}`
    : p.at_least === 0 ? `up to ${p.at_most}` : `${p.at_least} to ${p.at_most}`;
}

// What stands between the piles and Done, said as what to do about it; null when nothing does.
function misfitPile(a: PublicArrangement, piles: number[][]): string | null {
  for (const [j, p] of a.piles.entries()) {
    const n = piles[j]?.length ?? 0;
    const cards = (k: number) => `${k} ${k === 1 ? "card" : "cards"}`;
    const takes = p.at_least === p.at_most ? `exactly ${cards(p.at_most)}`
      : n > p.at_most ? `up to ${cards(p.at_most)}` : `at least ${cards(p.at_least)}`;
    if (n > p.at_most) return `${PILE_PLACE[p.to.kind].name} takes ${takes}. Move ${n - p.at_most} out.`;
    if (n < p.at_least) return `${PILE_PLACE[p.to.kind].name} takes ${takes}. Move ${p.at_least - n} in.`;
  }
  return null;
}
